import React from "react";
import clsx from "clsx";

// Material
import { makeStyles } from "@material-ui/core/styles";
import ButtonBase from "@material-ui/core/ButtonBase";

// Config
import { analyticsClassNames } from "../../config/googleAnalitycs";

// Assets
import mainImage from "../../assets/backgrounds/share-anonymously.png";
import newFeatureChip from "../../assets/icons/shareAnonymous/new-feature-chip.png";
import intelChip from "../../assets/icons/shareAnonymous/kulpa-i-chip.png";
import appStoreLogo from "../../assets/logos/AppStore_badge.svg";
import playStoreLogo from "../../assets/logos/Googleplay_badge.svg";
import elipse from "../../assets/backgrounds/elipse-purple.svg";

// Components
import Section from "../../components/Section/Section";
import Text, { types } from "../../components/Text/Text";
import { externalRoutes } from "../../config/urls";
import colors from "../../config/colors";
import { Typography } from "../../../node_modules/@material-ui/core/index";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "0 10vw",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: 0,
      padding: "0 6vw"
    },
    [`&:after`]: {
      content: '""',
      position: "absolute",
      right: 0,
      height: 383,
      width: 190,
      backgroundImage: `url(${elipse})`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
      backgroundSize: "contain",
      [theme.breakpoints.down("sm")]: {
        content: "none"
      }
    }
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: "120px 0 200px",
    [theme.breakpoints.down(1440)]: {
      padding: "80px 0 160px"
    },
    [theme.breakpoints.down("sm")]: {
      alignItems: "center",
      padding: "0 0 80px"
    }
  },
  newFeatureChip: {
    height: "50px",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down(1440)]: {
      height: "38px"
    },
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  newFeatureText: {
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    fontWeight: 600,
    fontSize: "14px",
    color: "#4129ED",
    textAlign: "center",
    marginBottom: "12px",
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block"
    }
  },
  title: {
    color: colors.mineShaft,
    maxWidth: "20ch",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      width: "100%",
      maxWidth: "30ch",
      marginBottom: "12px"
    }
  },
  labelWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(4)
    }
  },
  label: {
    fontSize: "38px",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    fontWeight: 300,
    [theme.breakpoints.down("md")]: {
      fontSize: "24px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "22px"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "20px",
      fontWeight: 500
    }
  },
  intelChip: {
    height: "50px",
    [theme.breakpoints.down("md")]: {
      height: "38px"
    },
    [theme.breakpoints.down(1440)]: {
      height: "28px"
    }
  },
  imageWrapper: {
    [theme.breakpoints.down("sm")]: {
      overflow: "hidden",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      maxWidth: "100vw"
    }
  },
  image: {
    maxWidth: "770px",
    [theme.breakpoints.down("md")]: {
      width: "35vw"
    },
    [theme.breakpoints.down("sm")]: {
      width: 640
    }
  },
  detail: {
    fontSize: "28px",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    fontWeight: "300",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      fontSize: "22px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: 18
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 16
    }
  },
  appStoresContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: theme.spacing(0, 2.25),
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(0, 1)
    },
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(0, 1)
    }
  },
  storeBadge: {
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: 245,
    [theme.breakpoints.down("md")]: {
      width: 190
    },
    [theme.breakpoints.down("sm")]: {
      width: 186
    },
    [theme.breakpoints.down("xs")]: {
      width: 170,
      maxWidth: "40vw"
    }
  },
  storeImage: {
    width: "100%"
  }
}));

const ShareAnonymous = () => {
  const classes = useStyles();

  return (
    <Section className={classes.root}>
      <div className={classes.imageWrapper}>
        <img src={mainImage} className={classes.image} alt="Get connected" />
      </div>
      <div className={classes.contentWrapper}>
        <img
          src={newFeatureChip}
          className={classes.newFeatureChip}
          alt="New Feature"
        />
        <Typography className={classes.newFeatureText}>NEW FEATURE</Typography>
        <Text type={types.homeSectionTitle} className={classes.title}>
          Do you want to share evidence anonymously with the police?
        </Text>
        <div className={classes.labelWrapper}>
          <Typography className={classes.label}>Now you can with</Typography>
          <img className={classes.intelChip} src={intelChip} alt="kulpa i" />
        </div>
        <Typography className={classes.detail}>
          Ready to give it a try
        </Typography>

        <div className={classes.appStoresContainer}>
          <ButtonBase
            aria-label="App Store"
            className={clsx(
              classes.storeBadge,
              analyticsClassNames.iosStoreLink
            )}
            component="a"
            href={externalRoutes.appStore}
            rel="noopener noreferrer"
            target="_blank"
          >
            <img src={appStoreLogo} alt="" className={classes.storeImage} />
          </ButtonBase>
          <ButtonBase
            aria-label="Google Play"
            className={clsx(
              classes.storeBadge,
              analyticsClassNames.androidStoreLink
            )}
            component="a"
            href={externalRoutes.googlePlay}
            rel="noopener noreferrer"
            target="_blank"
          >
            <img src={playStoreLogo} alt="" className={classes.storeImage} />
          </ButtonBase>
        </div>
      </div>
    </Section>
  );
};

export default ShareAnonymous;
